/**
 * PortraitContext - Centralized portrait resolution and caching.
 *
 * This context provides:
 * 1. Pre-loading of all character portraits when characters change
 * 2. Fast synchronous lookups by character ID or name
 * 3. Cache invalidation on portrait_updated socket events
 * 4. Leverages apiService.mediaUrlCache for blob URL caching
 *
 * Usage:
 *   // In a parent component (GameDashboard, PlayerPage, TavernPage):
 *   <PortraitProvider characters={characters} socket={socket}>
 *     <ChildComponents />
 *   </PortraitProvider>
 *
 *   // In child components:
 *   const { getPortraitUrl } = usePortraitContext();
 *   const resolvedUrl = getPortraitUrl(characterId, characterName);
 *
 *   // Or use the hook directly:
 *   const resolvedUrl = useResolvedPortrait(characterId, characterName, directUrl);
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import apiService from '../services/apiService.js';

// Context for portrait resolution
const PortraitContext = createContext(null);

// Storyteller/DM portrait
const STORYTELLER_PORTRAIT = '/dryad.webp';
const STORYTELLER_NAMES = ['storyteller', 'dm', 'dungeon master', 'st'];

/**
 * Is this directUrl a same-origin frontend asset that the browser should
 * resolve against window.location.origin? Returns true for site-relative
 * paths that the backend does not serve (e.g. /dryad.webp, /assets/foo.webp,
 * /favicon.ico). Returns false for /api/* and the legacy /media/* rewrite,
 * which DO need apiService.buildAbsoluteUrl + auth wrapping.
 *
 * Absolute URLs (http://, https://, blob:, data:) are not "site-relative"
 * and are not handled here — callers pass those through unchanged.
 */
const isFrontendStaticAsset = (directUrl) => {
  if (typeof directUrl !== 'string' || directUrl.length === 0) return false;
  if (!directUrl.startsWith('/')) return false;
  if (directUrl.startsWith('//')) return false; // protocol-relative absolute
  if (directUrl.startsWith('/api/')) return false;
  if (directUrl.startsWith('/media/')) return false;
  return true;
};

/**
 * Check if a name is a storyteller/DM name
 */
const isStorytellerName = (name) => {
  if (!name) return false;
  return STORYTELLER_NAMES.includes(name.toLowerCase().trim());
};

/**
 * Normalize character ID by stripping -copy-* suffix
 */
const normalizeCharacterId = (characterId) => {
  if (!characterId) return characterId;
  if (characterId.includes('-copy-')) {
    return characterId.split('-copy-')[0];
  }
  return characterId;
};

/**
 * Get cache key for a URL (same logic as apiService.fetchMediaAsObjectUrl)
 */
const getCacheKey = (url) => {
  if (!url) return null;
  try {
    const absoluteUrl = apiService.buildAbsoluteUrl(url);
    if (!absoluteUrl) return null;
    const urlObj = new URL(absoluteUrl);
    return urlObj.origin + urlObj.pathname;
  } catch {
    return url;
  }
};

/**
 * Check if a URL has a recent failure cached in apiService.
 */
const getFailureCacheEntry = (rawUrl) => {
  if (!rawUrl) return null;
  const cacheKey = getCacheKey(rawUrl);
  if (!cacheKey) return null;
  const failureCached = apiService.mediaUrlFailureCache?.get(cacheKey);
  if (!failureCached) return null;
  const ttl = apiService.mediaUrlFailureCacheTTL?.[failureCached.status]
    || apiService.mediaUrlFailureCacheTTL?.default
    || 30000;
  if (Date.now() - failureCached.timestamp < ttl) {
    return failureCached;
  }
  apiService.mediaUrlFailureCache?.delete(cacheKey);
  return null;
};

/**
 * Check if a URL is cached in apiService
 */
const isCached = (rawUrl) => {
  if (!rawUrl) return false;
  const cacheKey = getCacheKey(rawUrl);
  if (!cacheKey) return false;
  const cached = apiService.mediaUrlCache?.get(cacheKey);
  return cached && Date.now() - cached.timestamp < (apiService.mediaUrlCacheTTL || 300000);
};

/**
 * Get cached blob URL if available
 */
const getCachedBlobUrl = (rawUrl) => {
  if (!rawUrl) return null;
  const cacheKey = getCacheKey(rawUrl);
  if (!cacheKey) return null;
  if (getFailureCacheEntry(rawUrl)) {
    return null;
  }
  const cached = apiService.mediaUrlCache?.get(cacheKey);
  if (cached && Date.now() - cached.timestamp < (apiService.mediaUrlCacheTTL || 300000)) {
    return cached.blobUrl;
  }
  return null;
};

/**
 * PortraitProvider - Provides portrait resolution to child components.
 *
 * Props:
 * @param {Object} characters - Map of character_id -> CharacterInfo
 * @param {Object} socket - Socket instance for portrait_updated events
 * @param {React.ReactNode} children - Child components
 */
export function PortraitProvider({ characters, socket, children }) {
  // Version counter to force re-renders when cache is updated
  const [cacheVersion, setCacheVersion] = useState(0);

  // Track pending resolutions to avoid duplicate fetches
  const pendingResolutions = useRef(new Set());

  // Build lookup maps from characters for fast access
  const { byId, byName, allRawUrls } = useMemo(() => {
    const byId = new Map();
    const byName = new Map();
    const allRawUrls = new Map(); // rawUrl -> characterId (for invalidation)

    if (characters) {
      for (const [id, char] of Object.entries(characters)) {
        const rawUrl = char.portrait_url || char.portrait_path || char.portraitUrl || char.portraitPath || null;
        const name = (char.name || char.character_name || '').toLowerCase().trim();

        if (id) {
          byId.set(id, rawUrl);
          // Also store normalized ID
          const normalizedId = normalizeCharacterId(id);
          if (normalizedId !== id) {
            byId.set(normalizedId, rawUrl);
          }
        }

        if (name) {
          byName.set(name, rawUrl);
        }

        if (rawUrl) {
          allRawUrls.set(rawUrl, id);
        }
      }
    }

    return { byId, byName, allRawUrls };
  }, [characters]);

  /**
   * Pre-resolve all character portrait URLs on mount or when characters change
   */
  useEffect(() => {
    if (allRawUrls.size === 0) return;

    const resolveAll = async () => {
      const urlsToResolve = [];

      for (const rawUrl of allRawUrls.keys()) {
        // Skip if already cached or pending
        if (isCached(rawUrl) || getFailureCacheEntry(rawUrl) || pendingResolutions.current.has(rawUrl)) {
          continue;
        }
        urlsToResolve.push(rawUrl);
      }

      if (urlsToResolve.length === 0) return;

      // Mark all as pending
      for (const url of urlsToResolve) {
        pendingResolutions.current.add(url);
      }

      // Resolve all URLs in parallel
      let hadSuccess = false;
      try {
        const results = await Promise.allSettled(urlsToResolve.map(async (rawUrl) => {
          try {
            return await apiService.buildAuthorizedMediaUrl(rawUrl, { allowAbsoluteFallback: false });
          } catch (error) {
            if (!error?.cached) {
              console.warn('[PortraitContext] Failed to resolve portrait:', rawUrl, error);
            }
            throw error;
          }
        }));
        hadSuccess = results.some((result) => result.status === 'fulfilled');
        if (hadSuccess) {
          // Bump version to trigger re-renders with cached URLs
          setCacheVersion(v => v + 1);
        }
      } finally {
        for (const url of urlsToResolve) {
          pendingResolutions.current.delete(url);
        }
      }
    };

    resolveAll();
  }, [allRawUrls]);

  /**
   * Listen for portrait_updated socket events
   */
  useEffect(() => {
    if (!socket) return;

    const handlePortraitUpdated = (data) => {
      const { character_id, portrait_url } = data || {};

      if (character_id) {
        // Invalidate cache for this character's old URL
        const oldUrl = byId.get(character_id);
        if (oldUrl) {
          apiService.invalidateMediaCache(oldUrl);
        }

        // Bump version to trigger re-resolution
        setCacheVersion(v => v + 1);

        // If new URL provided, resolve it immediately
        if (portrait_url) {
          apiService.buildAuthorizedMediaUrl(portrait_url).catch(() => {});
        }
      }
    };

    socket.on('portrait_updated', handlePortraitUpdated);

    return () => {
      socket.off('portrait_updated', handlePortraitUpdated);
    };
  }, [socket, byId]);

  /**
   * Get resolved portrait URL for a character.
   * Returns immediately from cache if available.
   *
   * @param {string|null} characterId - Character ID
   * @param {string|null} characterName - Character name (fallback lookup)
   * @param {string|null} directUrl - Direct portrait URL (highest priority)
   * @returns {string|null} Resolved portrait URL or null
   */
  const getPortraitUrl = useCallback((characterId, characterName, directUrl = null) => {
    // Storyteller/DM gets the default portrait only when the caller didn't
    // supply a direct URL — custom narrative portraits must still win.
    if (!directUrl && isStorytellerName(characterName)) {
      return STORYTELLER_PORTRAIT;
    }

    // Same-origin frontend assets must bypass apiService.buildAbsoluteUrl,
    // which prefixes API_CONFIG.BACKEND_URL and 404s for anything the
    // backend doesn't serve (e.g. /dryad.webp, /assets/tavern/travelers/*,
    // /favicon.ico). Backend-served paths — /api/* and the legacy
    // /media/* rewrite — keep the existing resolution + auth pipeline.
    if (directUrl && isFrontendStaticAsset(directUrl)) {
      return directUrl;
    }

    // Priority 1: Direct URL if provided
    let rawUrl = directUrl;

    // Priority 2: Lookup by character ID
    if (!rawUrl && characterId) {
      rawUrl = byId.get(characterId);
      if (!rawUrl) {
        // Try normalized ID
        const normalizedId = normalizeCharacterId(characterId);
        rawUrl = byId.get(normalizedId);
      }
    }

    // Priority 3: Lookup by name
    if (!rawUrl && characterName) {
      const normalizedName = characterName.toLowerCase().trim();
      rawUrl = byName.get(normalizedName);
    }

    if (!rawUrl) {
      return null;
    }

    // Return cached blob URL if available
    const cachedBlobUrl = getCachedBlobUrl(rawUrl);
    if (cachedBlobUrl) {
      return cachedBlobUrl;
    }

    const failureEntry = getFailureCacheEntry(rawUrl);
    if (failureEntry) {
      // Preserve a direct URL fallback when auth/blob resolution fails (common in
      // local cookie-auth/dev flows) so portraits do not flash and disappear.
      return apiService.buildAbsoluteUrl(rawUrl);
    }

    // Trigger async resolution if not pending
    if (!pendingResolutions.current.has(rawUrl)) {
      pendingResolutions.current.add(rawUrl);
      apiService.buildAuthorizedMediaUrl(rawUrl, { allowAbsoluteFallback: false })
        .then(() => {
          setCacheVersion(v => v + 1);
        })
        .catch(() => {})
        .finally(() => {
          pendingResolutions.current.delete(rawUrl);
        });
    }

    // Return absolute URL as immediate fallback
    return apiService.buildAbsoluteUrl(rawUrl);
  }, [byId, byName, cacheVersion]); // cacheVersion dependency ensures we re-render when cache updates

  /**
   * Force refresh all portraits (e.g., after bulk portrait generation)
   */
  const refreshPortraits = useCallback(() => {
    // Invalidate all cached portrait URLs
    for (const rawUrl of allRawUrls.keys()) {
      apiService.invalidateMediaCache(rawUrl);
    }
    setCacheVersion(v => v + 1);
  }, [allRawUrls]);

  const contextValue = useMemo(() => ({
    getPortraitUrl,
    refreshPortraits,
    isReady: true, // Always ready - returns fallback URLs immediately
  }), [getPortraitUrl, refreshPortraits]);

  return (
    <PortraitContext.Provider value={contextValue}>
      {children}
    </PortraitContext.Provider>
  );
}

/**
 * Hook to access portrait context.
 * Works both inside and outside PortraitProvider.
 *
 * @returns {{ getPortraitUrl: Function, refreshPortraits: Function, isReady: boolean }}
 */
export function usePortraitContext() {
  const context = useContext(PortraitContext);
  const [fallbackCacheVersion, setFallbackCacheVersion] = useState(0);
  const fallbackPendingResolutions = useRef(new Set());

  const fallbackGetPortraitUrl = useCallback((characterId, characterName, directUrl) => {
    if (!directUrl && isStorytellerName(characterName)) {
      return STORYTELLER_PORTRAIT;
    }

    if (!directUrl) {
      return null;
    }

    // Same-origin frontend assets bypass backend resolution — see the
    // in-context getPortraitUrl above for the rationale.
    if (isFrontendStaticAsset(directUrl)) {
      return directUrl;
    }

    const normalizedUrl = apiService.resolveImageUrl(directUrl) || directUrl;
    const absoluteUrl = apiService.buildAbsoluteUrl(normalizedUrl);
    if (!absoluteUrl) {
      return null;
    }

    const needsAuth = absoluteUrl.includes('/api/media/') || absoluteUrl.includes('/api/images/');
    if (!needsAuth) {
      return absoluteUrl;
    }

    const cachedBlobUrl = getCachedBlobUrl(normalizedUrl);
    if (cachedBlobUrl) {
      return cachedBlobUrl;
    }

    const failureEntry = getFailureCacheEntry(normalizedUrl);
    if (failureEntry) {
      return apiService.buildAbsoluteUrl(normalizedUrl);
    }

    if (!fallbackPendingResolutions.current.has(normalizedUrl)) {
      fallbackPendingResolutions.current.add(normalizedUrl);
      apiService.buildAuthorizedMediaUrl(normalizedUrl, { allowAbsoluteFallback: false })
        .then(() => {
          setFallbackCacheVersion(v => v + 1);
        })
        .catch(() => {})
        .finally(() => {
          fallbackPendingResolutions.current.delete(normalizedUrl);
        });
    }

    return null;
  }, [fallbackCacheVersion]);

  if (!context) {
    // Return a fallback that resolves protected URLs with auth.
    // This allows components to work outside of PortraitProvider.
    return {
      getPortraitUrl: fallbackGetPortraitUrl,
      refreshPortraits: () => {},
      isReady: true,
    };
  }

  return context;
}

/**
 * Hook for getting a single portrait URL with automatic resolution.
 * Convenient wrapper around usePortraitContext for single lookups.
 *
 * @param {string|null} characterId - Character ID
 * @param {string|null} characterName - Character name
 * @param {string|null} directUrl - Direct portrait URL
 * @returns {string|null} Resolved portrait URL
 */
export function useResolvedPortrait(characterId, characterName, directUrl = null) {
  const { getPortraitUrl } = usePortraitContext();
  return getPortraitUrl(characterId, characterName, directUrl);
}

export default PortraitContext;
